import React, { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";

import { useAppStore } from "@/core/store/useAppStore";
import { formatDate } from "@/utils/date";
import { PageLayout, PageHeader, PageSection } from "@/components/PageLayout";
import { TabGroup, TabItem, TabGroupConfig } from "@/components/ui/TabGroup";
import { BoltIcon, ChevronDownIcon, SpinnerIcon } from "@/components/icons";
import { Button } from "@/components/ui/Button";
import { DropdownMenu } from "@/components/ui/DropdownMenu";
import { ContextMenu } from "@/components/MediaCard/ContextMenu";
import { PostMediaThumbnail } from "@/domains/PostCreator/cards/PostMediaThumbnail";
import { usePosts } from "@/domains/PostCreator/hooks/usePosts";
import type { Post } from "@/core/types/postTypes";

import { adsService } from "./services/adsService";
import {
  useLiveCampaigns,
  useInvalidateLiveCampaigns,
  usePastCampaigns,
} from "./hooks/useLiveCampaigns";
import { useBoostCandidates } from "./hooks/useBoostCandidates";
import { useAdAnalyticsSummary } from "./hooks/useAdAnalyticsSummary";
import { CampaignDetailsModal } from "./components/CampaignDetailsModal";
import { BoostModal } from "./components/BoostModal";
import { BaselineDelta } from "./components/BaselineDelta";
import type {
  AdAnalyticsSummary,
  BoostCandidate,
  BoostCandidatesView,
  BudgetView,
  Campaign,
  ImportExternalCampaignsResponse,
} from "./types";

type AdsTabId = "candidates" | "live" | "past";

const VALID_TAB_IDS: readonly AdsTabId[] = ["candidates", "live", "past"];
const DEFAULT_TAB: AdsTabId = "candidates";

const isValidTab = (value: string | null): value is AdsTabId =>
  value !== null && (VALID_TAB_IDS as readonly string[]).includes(value);

export const AdsView: React.FC = () => {
  const { selectedClient } = useAppStore();
  const clientId = selectedClient?.id;

  const budgetQuery = useQuery({
    queryKey: ["ads", "budget", clientId ?? "none"],
    queryFn: () => adsService.getBudget(clientId!),
    enabled: !!clientId,
    staleTime: 30_000,
  });

  const campaignsQuery = useLiveCampaigns(clientId);
  const pastCampaignsQuery = usePastCampaigns(clientId);
  const candidatesQuery = useBoostCandidates(clientId);
  // Feeds the per-campaign baseline-delta chips in the live campaigns
  // table. The aggregate strip lives on /reporting now; here we only
  // need the client's lifetime baseline as a reference.
  const analyticsQuery = useAdAnalyticsSummary(clientId, "live");
  const { posts, isLoading: postsLoading } = usePosts({});

  const postsById = useMemo(() => {
    const map = new Map<string, Post>();
    for (const p of posts) {
      map.set(p.id, p);
    }
    return map;
  }, [posts]);

  // Tab state lives in the URL (?tab=candidates|live) so reloads,
  // back/forward, and shared links land on the same tab.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const activeTab: AdsTabId = isValidTab(tabParam) ? tabParam : DEFAULT_TAB;
  const setActiveTab = useCallback(
    (id: AdsTabId) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (id === DEFAULT_TAB) {
            next.delete("tab");
          } else {
            next.set("tab", id);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [boostPostId, setBoostPostId] = useState<string | null>(null);
  // Force a fresh agent run when reopened via "Re-run agent". Bumping
  // this re-keys BoostModal so its internal hook resets and recommend
  // is called with force=true via the prop below.
  const [boostForceRerunNonce, setBoostForceRerunNonce] = useState(0);

  const openCandidateModal = (postId: string) => {
    setBoostForceRerunNonce(0);
    setBoostPostId(postId);
  };
  const openCandidateModalRerun = (postId: string) => {
    setBoostForceRerunNonce((n) => n + 1);
    setBoostPostId(postId);
  };

  const candidateCount = candidatesQuery.data?.candidates.length ?? 0;
  const liveCount = campaignsQuery.data?.length ?? 0;
  const pastCount = pastCampaignsQuery.data?.length ?? 0;

  const tabItems: TabItem<AdsTabId>[] = [
    { id: "candidates", label: "Boost candidates", badge: candidateCount },
    { id: "live", label: "Live campaigns", badge: liveCount },
    { id: "past", label: "Past campaigns", badge: pastCount },
  ];

  const tabConfig: TabGroupConfig<AdsTabId> = {
    variant: "navigation",
    size: "sm",
    layout: "horizontal",
    hasBackground: true,
  };

  if (!clientId) {
    return (
      <PageLayout>
        <PageSection.Padded>
          <p className="text-sm text-gray-500">Select a client to view ads.</p>
        </PageSection.Padded>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <PageHeader>
        <PageHeader.Row>
          <TabGroup
            items={tabItems}
            activeId={activeTab}
            onTabChange={setActiveTab}
            config={tabConfig}
          />
          <PageHeader.Actions>
            <ImportMetaCampaignsButton clientId={clientId} />
          </PageHeader.Actions>
        </PageHeader.Row>
      </PageHeader>

      <div className="px-4 lg:px-6 pb-4 shrink-0">
        <BudgetStrip query={budgetQuery} />
      </div>

      <PageSection.Card>
        {activeTab === "candidates" ? (
          <BoostCandidatesTable
            query={candidatesQuery}
            postsById={postsById}
            postsLoading={postsLoading}
            onOpen={openCandidateModal}
            onRerun={openCandidateModalRerun}
          />
        ) : activeTab === "live" ? (
          <CampaignsTable
            query={campaignsQuery}
            postsById={postsById}
            onSelect={setSelectedCampaign}
            baselines={analyticsQuery.data?.baselines}
            emptyTitle="No live campaigns"
            emptyBody="Nothing is running right now. Boost a candidate or import campaigns from Meta to populate this list."
          />
        ) : (
          <CampaignsTable
            query={pastCampaignsQuery}
            postsById={postsById}
            onSelect={setSelectedCampaign}
            baselines={analyticsQuery.data?.baselines}
            emptyTitle="No past campaigns"
            emptyBody="Finished and cancelled campaigns will appear here once any campaigns have run their course."
          />
        )}
      </PageSection.Card>

      <CampaignDetailsModal
        campaign={selectedCampaign}
        isOpen={!!selectedCampaign}
        onClose={() => setSelectedCampaign(null)}
      />

      <BoostModal
        // Re-keying the modal forces a fresh hook lifecycle — needed
        // for the "Re-run agent" path so the cached recommendation
        // doesn't shortcut a forced refresh.
        key={`${boostPostId ?? ""}-${boostForceRerunNonce}`}
        postId={boostPostId}
        forceRerunOnOpen={boostForceRerunNonce > 0}
        isOpen={!!boostPostId}
        onClose={() => setBoostPostId(null)}
      />
    </PageLayout>
  );
};

// ----- Header actions --------------------------------------------------

const ImportMetaCampaignsButton: React.FC<{ clientId: string }> = ({
  clientId,
}) => {
  const addToast = useAppStore((s) => s.addToast);
  const invalidateLiveCampaigns = useInvalidateLiveCampaigns();
  const queryClient = useQueryClient();
  const [isImporting, setIsImporting] = useState(false);

  const runImport = async (days: 30 | 60 | 90) => {
    if (isImporting) return;
    setIsImporting(true);
    try {
      const result: ImportExternalCampaignsResponse =
        await adsService.importExternalCampaigns(clientId, days);
      const s = result.stats;
      addToast({
        type: s.errors.length > 0 ? "warning" : "success",
        message: `Imported ${s.imported} campaign${s.imported === 1 ? "" : "s"} (${s.matchedToPost} linked, ${s.unmatchedPost} unmatched). ${s.skippedExisting} already known.`,
        duration: 6000,
      });
      invalidateLiveCampaigns(clientId);
      queryClient.invalidateQueries({
        queryKey: ["ads", "boost-candidates", clientId],
      });
      queryClient.invalidateQueries({
        queryKey: ["ads", "budget", clientId],
      });
    } catch (err) {
      addToast({
        type: "error",
        message:
          err instanceof Error
            ? err.message
            : "Failed to import Meta campaigns",
        duration: 6000,
      });
    } finally {
      setIsImporting(false);
    }
  };

  const items = ([30, 60, 90] as const).map((d) => ({
    id: `import-${d}`,
    label: `Last ${d} days`,
    onClick: () => runImport(d),
    disabled: isImporting,
  }));

  return (
    <DropdownMenu
      items={items}
      width="w-40"
      trigger={
        <Button variant="secondary" size="sm" isLoading={isImporting}>
          <span className="flex items-center gap-1">
            Import Meta campaigns
            <ChevronDownIcon className="w-3.5 h-3.5" />
          </span>
        </Button>
      }
    />
  );
};

// ----- Budget strip ----------------------------------------------------

const BudgetStrip: React.FC<{ query: UseQueryResult<BudgetView> }> = ({
  query,
}) => {
  if (query.isLoading) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white px-5 py-4">
        <Loading />
      </div>
    );
  }

  if (query.isError) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white px-5 py-4">
        <p className="text-sm text-red-600">Failed to load budget.</p>
      </div>
    );
  }

  const data = query.data;
  if (!data || !data.available) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white px-5 py-4">
        <p className="text-sm text-gray-500">
          No monthly budget configured for this client yet.
        </p>
      </div>
    );
  }

  const fmt = currencyFormatter(data.currency);
  const total = data.totalBudget ?? 0;
  const allocated = data.allocated ?? 0;
  const remaining = data.remaining ?? 0;
  const pctUsed = total > 0 ? Math.min(100, (allocated / total) * 100) : 0;

  return (
    <div className="rounded-lg border border-gray-200 bg-white px-5 py-4">
      <div className="flex items-center justify-between gap-6">
        <div className="grid grid-cols-3 gap-8 flex-1">
          <Stat label="Monthly budget" value={fmt.format(total)} />
          <Stat label="Allocated" value={fmt.format(allocated)} />
          <Stat
            label="Remaining"
            value={fmt.format(remaining)}
            accent={remaining > 0 ? "text-green-700" : "text-gray-900"}
          />
        </div>
        {data.perCampaignCap != null && (
          <div className="text-xs text-gray-500 shrink-0">
            Per-campaign cap: {fmt.format(data.perCampaignCap)}
          </div>
        )}
      </div>
      <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
        <div
          className="h-full bg-blue-500 transition-all"
          style={{ width: `${pctUsed}%` }}
        />
      </div>
    </div>
  );
};

// ----- Boost candidates table -----------------------------------------

const BoostCandidatesTable: React.FC<{
  query: UseQueryResult<BoostCandidatesView>;
  postsById: Map<string, Post>;
  postsLoading: boolean;
  onOpen: (postId: string) => void;
  onRerun: (postId: string) => void;
}> = ({ query, postsById, postsLoading, onOpen, onRerun }) => {
  if (query.isLoading || postsLoading) {
    return <TableLoading />;
  }

  const data = query.data;
  if (!data || data.candidates.length === 0) {
    return (
      <EmptyState
        title="No boost candidates"
        body="Commercial posts surface automatically once they clear the reach floor (200) and engagement floor (10). Non-commercial posts also need to beat the 30-day baseline engagement rate by 20%."
      />
    );
  }

  const fmt = currencyFormatter(data.currency);

  return (
    <div className="flex-1 min-h-0 overflow-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50 sticky top-0 z-10">
          <tr>
            <th className="px-4 py-3 w-20" />
            <Th>Post</Th>
            <Th>Lift</Th>
            <Th>Engagement</Th>
            <Th>Reach</Th>
            <Th>Suggested budget</Th>
            <Th align="right">Actions</Th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {data.candidates.map((c) => (
            <CandidateRow
              key={c.postId}
              candidate={c}
              post={postsById.get(c.postId)}
              fmt={fmt}
              onOpen={onOpen}
              onRerun={onRerun}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};

const CandidateRow: React.FC<{
  candidate: BoostCandidate;
  post: Post | undefined;
  fmt: Intl.NumberFormat;
  onOpen: (postId: string) => void;
  onRerun: (postId: string) => void;
}> = ({ candidate, post, fmt, onOpen, onRerun }) => {
  const ratePct = (candidate.engagementRate * 100).toFixed(1);
  const liftPct = ((candidate.lift - 1) * 100).toFixed(0);
  const suggested = candidate.suggestedTotalBudget;
  const lifetimeSpend = candidate.lifetimeSpend ?? 0;

  const menuItems = [
    {
      id: "open",
      label: "Open recommendation",
      icon: <BoltIcon className="w-4 h-4" />,
      onClick: () => onOpen(candidate.postId),
    },
    {
      id: "rerun",
      label: "Re-run agent",
      icon: <SpinnerIcon className="w-4 h-4" />,
      onClick: () => onRerun(candidate.postId),
    },
  ];

  return (
    <tr
      className="hover:bg-gray-50 cursor-pointer"
      onClick={() => onOpen(candidate.postId)}
    >
      <td className="px-4 py-3 align-top">
        {post ? (
          <PostMediaThumbnail post={post} />
        ) : (
          <PostThumbPlaceholder />
        )}
      </td>
      <td className="px-4 py-3 align-middle">
        <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
          {labelForPostType(candidate.postType)}
          <CandidateReasonBadge
            isCommercial={candidate.isCommercial}
            liftPct={liftPct}
          />
        </div>
        {candidate.publishedAt && (
          <div className="text-xs text-gray-500 mt-0.5">
            Published {formatDate(candidate.publishedAt)}
          </div>
        )}
        {lifetimeSpend > 0 && (
          <div
            className="text-[11px] text-gray-500 mt-1 inline-flex items-center gap-1"
            title="Cumulative spend across every past campaign on this post"
          >
            <span className="opacity-70">Already spent:</span>
            <span className="font-semibold text-gray-700 tabular-nums">
              {fmt.format(lifetimeSpend)}
            </span>
          </div>
        )}
      </td>
      <td className="px-4 py-3 align-middle">
        <LiftCell
          liftPct={liftPct}
          isCommercial={candidate.isCommercial}
          baselinePct={candidate.baselineRate * 100}
        />
      </td>
      <td className="px-4 py-3 align-middle">
        <div className="text-sm text-gray-900">{ratePct}%</div>
        <div className="text-xs text-gray-500">
          {candidate.engagementTotal.toLocaleString()} total
        </div>
      </td>
      <td className="px-4 py-3 align-middle text-sm text-gray-900">
        {candidate.reachTotal.toLocaleString()}
      </td>
      <td className="px-4 py-3 align-middle">
        {suggested > 0 ? (
          <>
            <div className="text-sm text-gray-900">{fmt.format(suggested)}</div>
            <div className="text-xs text-gray-500">
              {fmt.format(candidate.suggestedDailyBudget)}/day ·{" "}
              {candidate.suggestedDurationDays}d
            </div>
          </>
        ) : (
          <span className="text-xs text-gray-500">No budget left</span>
        )}
      </td>
      <td
        className="px-4 py-3 align-middle text-right"
        onClick={(e) => e.stopPropagation()}
      >
        <ContextMenu items={menuItems} />
      </td>
    </tr>
  );
};

// ----- Campaigns table (live + past) ---------------------------------

const CampaignsTable: React.FC<{
  query: UseQueryResult<Campaign[]>;
  postsById: Map<string, Post>;
  onSelect: (c: Campaign) => void;
  baselines: AdAnalyticsSummary["baselines"] | undefined;
  emptyTitle: string;
  emptyBody: string;
}> = ({ query, postsById, onSelect, baselines, emptyTitle, emptyBody }) => {
  if (query.isLoading) {
    return <TableLoading />;
  }

  const items = query.data ?? [];
  if (items.length === 0) {
    return <EmptyState title={emptyTitle} body={emptyBody} />;
  }

  return (
    <div className="flex-1 min-h-0 overflow-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50 sticky top-0 z-10">
          <tr>
            <th className="px-4 py-3 w-20" />
            <Th>Platform</Th>
            <Th>Status</Th>
            <Th>Source</Th>
            <Th>Schedule</Th>
            <Th>Impressions</Th>
            <Th>Clicks</Th>
            <Th>CTR</Th>
            <Th>CPM</Th>
            <Th>CPC</Th>
            <Th>Spend</Th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {items.map((c) => (
            <CampaignRow
              key={c.id}
              campaign={c}
              post={c.postId ? postsById.get(c.postId) : undefined}
              onSelect={onSelect}
              baselines={baselines}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};

const CampaignRow: React.FC<{
  campaign: Campaign;
  post: Post | undefined;
  onSelect: (c: Campaign) => void;
  baselines: AdAnalyticsSummary["baselines"] | undefined;
}> = ({ campaign, post, onSelect, baselines }) => {
  const allocated = Number(campaign.budgetAllocated ?? 0);
  const spent = Number(campaign.budgetSpent ?? 0);
  const pctSpent =
    allocated > 0 ? Math.min(100, (spent / allocated) * 100) : 0;
  const perf = campaign.performance;
  const sample = baselines?.sampleCampaigns ?? 0;

  return (
    <tr
      className="hover:bg-gray-50 cursor-pointer"
      onClick={() => onSelect(campaign)}
    >
      <td className="px-4 py-3 align-top">
        {post ? (
          <PostMediaThumbnail post={post} />
        ) : (
          <PostThumbPlaceholder source={campaign.creativeSource} />
        )}
      </td>
      <td className="px-4 py-3 align-middle text-sm font-medium text-gray-900">
        {campaign.platform}
      </td>
      <td className="px-4 py-3 align-middle">
        <StatusPill status={campaign.status} />
      </td>
      <td className="px-4 py-3 align-middle">
        <SourcePill source={campaign.creativeSource} />
      </td>
      <td className="px-4 py-3 align-middle text-xs text-gray-600 whitespace-nowrap">
        {formatRange(campaign.startDate, campaign.endDate)}
      </td>
      <PerfCell value={perf?.impressions} />
      <PerfCell value={perf?.clicks} />
      <PerfCellWithDelta
        text={perf?.ctr != null ? `${(perf.ctr * 100).toFixed(2)}%` : null}
        rawForEmpty={perf?.impressions}
        delta={
          <BaselineDelta
            current={perf?.ctr ?? null}
            baseline={baselines?.ctr ?? null}
            sample={sample}
            higherIsBetter
            compact
          />
        }
      />
      <PerfCellWithDelta
        text={perf?.cpm != null ? formatPreciseMoney(perf.cpm) : null}
        rawForEmpty={perf?.impressions}
        delta={
          <BaselineDelta
            current={perf?.cpm ?? null}
            baseline={baselines?.cpm ?? null}
            sample={sample}
            higherIsBetter={false}
            compact
          />
        }
      />
      <PerfCellWithDelta
        text={perf?.cpc != null ? formatPreciseMoney(perf.cpc) : null}
        rawForEmpty={perf?.clicks}
        delta={
          <BaselineDelta
            current={perf?.cpc ?? null}
            baseline={baselines?.cpc ?? null}
            sample={sample}
            higherIsBetter={false}
            compact
          />
        }
      />
      <td className="px-4 py-3 align-middle min-w-[180px]">
        <div className="flex items-baseline gap-2 text-xs">
          <span className="font-medium text-gray-900">€{spent.toFixed(0)}</span>
          <span className="text-gray-500">of €{allocated.toFixed(0)}</span>
        </div>
        <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
          <div
            className="h-full bg-blue-500 transition-all"
            style={{ width: `${pctSpent}%` }}
          />
        </div>
      </td>
    </tr>
  );
};

const PerfCell: React.FC<{
  value: number | string | null | undefined;
  rawForEmpty?: number | null;
}> = ({ value, rawForEmpty }) => {
  const empty =
    value == null ||
    value === "" ||
    (typeof value === "number" && value === 0 && rawForEmpty == null);
  return (
    <td className="px-4 py-3 align-middle text-sm text-gray-900 tabular-nums whitespace-nowrap">
      {empty ? (
        <span className="text-gray-300">—</span>
      ) : typeof value === "number" ? (
        value.toLocaleString()
      ) : (
        value
      )}
    </td>
  );
};

const PerfCellWithDelta: React.FC<{
  text: string | null;
  rawForEmpty: number | null | undefined;
  delta: React.ReactNode;
}> = ({ text, rawForEmpty, delta }) => {
  const empty = text == null || (rawForEmpty == null);
  return (
    <td className="px-4 py-3 align-middle text-sm text-gray-900 tabular-nums whitespace-nowrap">
      {empty ? (
        <span className="text-gray-300">—</span>
      ) : (
        <div className="flex flex-col items-start gap-1">
          <span>{text}</span>
          {delta}
        </div>
      )}
    </td>
  );
};

const formatPreciseMoney = (n: number): string => {
  const fmt = new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: n >= 1 ? 0 : 2,
    maximumFractionDigits: n >= 1 ? 0 : 2,
  });
  return fmt.format(n);
};

// ----- Bits ------------------------------------------------------------

// Two reasons a post lands in the candidates table: it's commercial
// (promotional intent — eligible by design), or it's organic and
// outperforming the baseline. Spell out the reason so the user reads
// the row before clicking.
const CandidateReasonBadge: React.FC<{
  isCommercial: boolean;
  liftPct: string;
}> = ({ isCommercial, liftPct }) => {
  if (isCommercial) {
    return (
      <span
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 ring-1 ring-amber-200/80 text-[10px] font-semibold uppercase tracking-wide"
        title="Promotional post — always shown here regardless of organic performance"
      >
        <BoltIcon className="w-3 h-3" />
        Commercial
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200/80 text-[10px] font-semibold uppercase tracking-wide"
      title="Organic post — surfaced because it's outperforming baseline"
    >
      Top +{liftPct}%
    </span>
  );
};

// Lift pill shows the multiplier against baseline. Commercial posts
// don't need to beat the baseline to surface, so a lift < 0 still
// renders cleanly (with a neutral tone instead of green).
const LiftCell: React.FC<{
  liftPct: string;
  isCommercial: boolean;
  baselinePct: number;
}> = ({ liftPct, isCommercial, baselinePct }) => {
  const liftNum = Number(liftPct);
  const isPositive = liftNum > 0;
  const tone = isPositive
    ? "bg-emerald-100 text-emerald-800"
    : isCommercial
      ? "bg-gray-100 text-gray-600"
      : "bg-red-50 text-red-700";
  const sign = isPositive ? "+" : "";
  return (
    <div>
      <span
        className={`inline-flex items-center rounded-md text-xs font-semibold px-2 py-0.5 ${tone}`}
      >
        {sign}
        {liftPct}%
      </span>
      <div className="text-[11px] text-gray-500 mt-0.5 tabular-nums">
        baseline {baselinePct.toFixed(1)}%
      </div>
    </div>
  );
};

const Th: React.FC<{
  children: React.ReactNode;
  align?: "left" | "right";
}> = ({ children, align = "left" }) => (
  <th
    className={`px-4 py-3 text-${align} text-xs font-medium text-gray-500 uppercase tracking-wider`}
  >
    {children}
  </th>
);

const Stat: React.FC<{ label: string; value: string; accent?: string }> = ({
  label,
  value,
  accent = "text-gray-900",
}) => (
  <div>
    <div className="text-xs uppercase tracking-wide text-gray-500">{label}</div>
    <div className={`mt-1 text-xl font-semibold ${accent}`}>{value}</div>
  </div>
);

const Loading: React.FC = () => (
  <div className="flex items-center gap-2 text-sm text-gray-500">
    <SpinnerIcon className="w-4 h-4" />
    Loading…
  </div>
);

const TableLoading: React.FC = () => (
  <div className="flex items-center justify-center flex-1 py-16">
    <Loading />
  </div>
);

const EmptyState: React.FC<{ title: string; body: string }> = ({
  title,
  body,
}) => (
  <div className="flex flex-col items-center justify-center flex-1 py-16 px-6 text-center">
    <div className="text-gray-400 mb-3">
      <BoltIcon className="w-12 h-12" />
    </div>
    <p className="text-sm font-medium text-gray-700">{title}</p>
    <p className="text-sm text-gray-500 mt-1 max-w-md">{body}</p>
  </div>
);

const StatusPill: React.FC<{ status: Campaign["status"] }> = ({ status }) => {
  const tone =
    status === "ACTIVE"
      ? "bg-green-100 text-green-800"
      : status === "PAUSED"
        ? "bg-yellow-100 text-yellow-800"
        : status === "CREATING"
          ? "bg-blue-100 text-blue-800"
          : status === "FAILED"
            ? "bg-red-100 text-red-800"
            : "bg-gray-100 text-gray-700";
  return (
    <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${tone}`}>
      {status}
    </span>
  );
};

// String compare instead of typed enum so future creative_source values
// render correctly without a type bump here.
const SourcePill: React.FC<{ source: string }> = ({ source }) => {
  if (source === "external_import") {
    return (
      <span className="text-xs px-1.5 py-0.5 rounded bg-purple-100 text-purple-800 font-medium">
        External
      </span>
    );
  }
  if (source === "standalone_ad") {
    return (
      <span className="text-xs px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-800 font-medium">
        Standalone
      </span>
    );
  }
  return (
    <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 font-medium">
      Boost
    </span>
  );
};

// Width + aspect deliberately match PostMediaThumbnail's defaults so
// rows stay consistent whether the post resolved or not.
const PostThumbPlaceholder: React.FC<{ source?: string }> = ({ source }) => (
  <div className="relative shrink-0">
    <div className="w-24 aspect-4/5 rounded-xl border border-dashed border-gray-300 bg-gray-50 flex flex-col items-center justify-center text-center text-[11px] text-gray-400 p-2">
      <BoltIcon className="w-5 h-5 mb-1" />
      {source === "external_import"
        ? "External"
        : source === "standalone_ad"
          ? "Standalone"
          : "No post"}
    </div>
  </div>
);

const formatRange = (start: string | null, end: string | null): string => {
  if (!start && !end) return "No schedule";
  const s = formatDate(start, { fallback: "n/a" });
  const e = formatDate(end, { fallback: "ongoing" });
  return `${s} → ${e}`;
};

const labelForPostType = (postType: string): string => {
  if (postType === "FEED_POST") return "Feed post";
  if (postType === "REEL") return "Reel";
  if (postType === "STORY") return "Story";
  return postType;
};

const currencyFormatter = (currency?: string): Intl.NumberFormat =>
  new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: currency || "EUR",
    maximumFractionDigits: 0,
  });
